import { motion } from 'framer-motion';
import { useInView } from '../../../hooks/useInView';
import { Link } from 'react-router-dom';

const CranesSection = () => {
  const [ref, isInView] = useInView({ threshold: 0.2 });

  return (
    <section id="cranes" className="py-24 bg-white">
      <div className="container mx-auto px-4 lg:px-8" ref={ref}>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-2 h-2 bg-accent rounded-full"></div>
            <span className="text-accent font-inter font-semibold text-sm uppercase tracking-wider">Our Fleet</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-poppins font-black text-primary mb-6">
            Premium Crane Solutions
          </h2>
          <p className="text-neutral-500 font-inter text-lg max-w-2xl mx-auto">
            From light-duty to heavy-duty cranes, we provide the right equipment for every project
          </p>
        </motion.div>

        {/* Heavy Cranes */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-16"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div className="relative h-[500px] rounded-3xl overflow-hidden group">
              <img
                src="https://public.readdy.ai/ai/img_res/c0b81387b92b7deab467cff53d6111f6.jpg"
                alt="Heavy Cranes"
                className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
              <div className="absolute bottom-8 left-8 right-8">
                <div className="flex items-center gap-4 mb-4">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/8/8e/Sany_logo.svg/320px-Sany_logo.svg.png" alt="SANY" className="h-8 brightness-0 invert" />
                  <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/f/f5/XCMG_logo.svg/320px-XCMG_logo.svg.png" alt="XCMG" className="h-8 brightness-0 invert" />
                  <img src="https://www.tadano.com/shared/images/common/logo.svg" alt="Tadano" className="h-8 brightness-0 invert" />
                </div>
              </div>
            </div>

            {/* Content */}
            <div>
              <div className="inline-block px-4 py-2 bg-accent/10 rounded-full mb-6">
                <span className="text-accent font-inter font-bold text-sm uppercase tracking-wider">Premium Fleet</span>
              </div>
              <h3 className="text-4xl md:text-5xl font-poppins font-black text-primary mb-6">
                Heavy Duty Cranes
              </h3>
              <p className="text-neutral-600 font-inter text-base leading-relaxed mb-8">
                Our heavy-duty crane fleet features top-tier equipment from industry leaders SANY, XCMG, and Tadano. 
                With lifting capacities ranging from <strong className="text-accent">25 to 300 tons</strong>, 
                we handle the most demanding construction and infrastructure projects across Saudi Arabia.
              </p>

              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-weight-line text-2xl text-accent"></i>
                  </div>
                  <div>
                    <div className="font-poppins font-bold text-2xl text-primary">25-300</div>
                    <div className="text-neutral-500 font-inter text-sm">Ton Capacity</div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-2xl text-accent"></i>
                  </div>
                  <div>
                    <div className="font-poppins font-bold text-2xl text-primary">100%</div>
                    <div className="text-neutral-500 font-inter text-sm">Safety Certified</div>
                  </div>
                </div>
              </div>

              <Link
                to="/cranes/heavy"
                className="inline-flex items-center gap-3 px-8 py-4 bg-primary text-white rounded-full font-inter font-bold text-sm hover:bg-primary-dark transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                View Heavy Cranes
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-lg"></i>
                </div>
              </Link>
            </div>
          </div>
        </motion.div>

        {/* Light Cranes */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <div className="order-2 lg:order-1">
              <div className="inline-block px-4 py-2 bg-accent/10 rounded-full mb-6">
                <span className="text-accent font-inter font-bold text-sm uppercase tracking-wider">Versatile Solutions</span>
              </div>
              <h3 className="text-4xl md:text-5xl font-poppins font-black text-primary mb-6">
                Light Duty Cranes
              </h3>
              <p className="text-neutral-600 font-inter text-base leading-relaxed mb-8">
                Perfect for small to medium-sized projects, our light-duty cranes offer exceptional maneuverability 
                and precision. With capacities from <strong className="text-accent">3 to 19 tons</strong>, 
                these versatile machines are ideal for residential, commercial, and light industrial applications.
              </p>

              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-weight-line text-2xl text-accent"></i>
                  </div>
                  <div>
                    <div className="font-poppins font-bold text-2xl text-primary">3-19</div>
                    <div className="text-neutral-500 font-inter text-sm">Ton Capacity</div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-speed-line text-2xl text-accent"></i>
                  </div>
                  <div>
                    <div className="font-poppins font-bold text-2xl text-primary">Fast</div>
                    <div className="text-neutral-500 font-inter text-sm">Deployment</div>
                  </div>
                </div>
              </div>

              <Link
                to="/cranes/light"
                className="inline-flex items-center gap-3 px-8 py-4 bg-accent text-white rounded-full font-inter font-bold text-sm hover:bg-accent-dark transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                View Light Cranes
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-lg"></i>
                </div>
              </Link>
            </div>

            {/* Image */}
            <div className="relative h-[500px] rounded-3xl overflow-hidden group order-1 lg:order-2">
              <img
                src="https://readdy.ai/api/search-image?query=compact%20mobile%20crane%20SANY%20or%20XCMG%20light%20duty%20crane%20working%20on%20residential%20construction%20site%2C%20professional%20construction%20equipment%20photography%2C%2015%20ton%20capacity%20crane%20with%20telescopic%20boom%2C%20modern%20construction%20machinery%20in%20urban%20setting%2C%20operator%20working%20safely%2C%20bright%20daylight%20professional%20photography%2C%20three%20quarter%20angle%20showing%20crane%20maneuverability%20and%20compact%20design&width=800&height=600&seq=light-crane-main-001&orientation=landscape"
                alt="Light Cranes"
                className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
              <div className="absolute bottom-8 left-8 right-8">
                <div className="flex items-center gap-4 mb-4">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/8/8e/Sany_logo.svg/320px-Sany_logo.svg.png" alt="SANY" className="h-8 brightness-0 invert" />
                  <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/f/f5/XCMG_logo.svg/320px-XCMG_logo.svg.png" alt="XCMG" className="h-8 brightness-0 invert" />
                  <img src="https://www.tadano.com/shared/images/common/logo.svg" alt="Tadano" className="h-8 brightness-0 invert" />
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CranesSection;
